import * as _ from 'lodash-es';
import { BlockEmbed } from 'quill/blots/block';
import Quill, { AttributeMap, Delta } from 'quill/core';
import React from 'react';
import { MenuItem, MenuSeparator } from '../../../components/Menu';
import attachmentIcon from '../../../icons/non-prefilled/16x16/attachment.svg';
import downloadIcon from '../../../icons/non-prefilled/16x16/download.svg';
import newTabIcon from '../../../icons/non-prefilled/16x16/newTab.svg';
import useIsPublicView from '../../../utils/useIsPublicView';
import FileBlot from '../../formats/FileBlot';
import type { HoveredBlotListenerSetting } from '../../HoveredBlotListener';
import { useHoveredBlotListener } from '../../HoveredBlotListener';
import { useQuill } from '../../QuillContext';
import type { Blot } from '../../types';
import getDownloadLink from '../../utils/getDownloadLink';
import replaceEmbedBlotWith from '../../utils/replaceEmbedBlotWith';
import ConvertIntoMentionMenuItem from '../ConvertIntoMentionMenuItem';
import ToolbarCommentButtonGroup from './buttons/ToolbarCommentButtonGroup';
import ToolbarHeightButtonGroup from './buttons/ToolbarHeightButtonGroup';
import ToolbarSpanButtonGroup from './buttons/ToolbarSpanButtonGroup';
import DeleteMenuItem from './DeleteMenuItem';
import FloatingToolbar from './FloatingToolbar';
import Toolbar, { ToolbarOverflowMenu } from './Toolbar';
import useBlotHighlightedClass from './useBlotHighlightedClass';

type UpdateFile = (change: { attributes: AttributeMap }) => void;

interface FileToolbarOverflowMenuProps {
  blot: FileBlot;
}

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const FileToolbarOverflowMenu = ({
  blot,
}: FileToolbarOverflowMenuProps) => {
  const { quill } = useQuill();

  const file = blot.getAsAttachmentFile();
  const isUploading = blot.isUploading();
  const downloadLink = !isUploading && getDownloadLink(file);

  return (
    <>
      {!isUploading && (
        <MenuItem key="openNewTab" icon={newTabIcon} newTab to={file.source}>
          Open in new tab
        </MenuItem>
      )}
      {quill && (
        <>
          <ConvertIntoMentionMenuItem blot={blot} quill={quill} />
          <MenuItem
            icon={attachmentIcon}
            onClick={() => {
              replaceEmbedBlotWith(
                quill,
                blot,
                {
                  attachment: [blot.getAsAttachmentFile()],
                },
                blot.formats(),
              );
            }}
            testId="convertFileIntoAttachmentMenuItem"
          >
            Convert into attachment
          </MenuItem>
        </>
      )}
      {downloadLink && (
        <MenuItem key="download" icon={downloadIcon} to={downloadLink} newTab>
          Download file
        </MenuItem>
      )}
      <>
        <MenuSeparator />
        <DeleteMenuItem onClick={() => blot.remove()}>
          Remove this file
        </DeleteMenuItem>
      </>
    </>
  );
};

interface BaseFileToolbarProps {
  hoveredBlot: FileBlot;
  updateFile: UpdateFile;
}

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const BaseFileToolbar = ({
  hoveredBlot,
  updateFile,
}: BaseFileToolbarProps) => {
  const isPublicView = useIsPublicView();
  const { readOnly } = useQuill();
  useBlotHighlightedClass(hoveredBlot);
  const formats = hoveredBlot.formats();

  if (readOnly && isPublicView) {
    return null;
  }

  return (
    <Toolbar
      targetBlot={hoveredBlot}
      overflowMenu={
        readOnly ? undefined : (
          <ToolbarOverflowMenu>
            <FileToolbarOverflowMenu blot={hoveredBlot} />
          </ToolbarOverflowMenu>
        )
      }
    >
      {!readOnly && (
        <>
          <ToolbarSpanButtonGroup
            allowHalfWidth={false}
            span={formats.span}
            onSpanChange={(newSpan) => {
              updateFile({ attributes: { span: newSpan } });
            }}
          />
          <ToolbarHeightButtonGroup
            height={formats.height}
            onHeightChange={(newHeight) =>
              updateFile({ attributes: { height: newHeight } })
            }
          />
        </>
      )}
      <ToolbarCommentButtonGroup
        blot={hoveredBlot}
        existingComment={formats.comment}
      />
    </Toolbar>
  );
};

const hoveredBlotListenerSetting: HoveredBlotListenerSetting<
  Record<string, unknown>,
  FileBlot
> = {
  isHoveredBlot(blot: Blot) {
    return blot.statics.blotName === FileBlot.blotName;
  },
  getContext() {
    return {};
  },
};

const FileToolbar = () => {
  const { quill } = useQuill();

  const { bounds, hoveredBlot } = useHoveredBlotListener(
    quill,
    hoveredBlotListenerSetting,
  );

  const updateFile: UpdateFile = (change) => {
    if (!(hoveredBlot instanceof BlockEmbed)) return;

    const delta = new Delta().retain(hoveredBlot.offset(hoveredBlot.scroll));
    const currentFormats = hoveredBlot.formats();
    const attributes = AttributeMap.diff(
      currentFormats,
      AttributeMap.compose(currentFormats, change.attributes, false),
    );

    if (quill && !_.isEmpty(attributes)) {
      quill.updateContents(delta.retain(1, attributes), Quill.sources.USER);
    }
  };

  return (
    <FloatingToolbar.Toolbar testId="fileToolbar" bounds={bounds}>
      {hoveredBlot && (
        <BaseFileToolbar hoveredBlot={hoveredBlot} updateFile={updateFile} />
      )}
    </FloatingToolbar.Toolbar>
  );
};

export default FileToolbar;
